import React, { useState, useEffect, useMemo, useRef } from "react";
import { useConfig } from "@peracto/peracto-config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBoxFull } from "@fortawesome/pro-regular-svg-icons/faBoxFull";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons/faArrowLeft";
import { faArrowRight } from "@fortawesome/pro-regular-svg-icons/faArrowRight";
import algoliasearch from "algoliasearch/lite";
import { InstantSearch, connectHits, Configure } from "react-instantsearch-dom";

import isEmpty from "lodash/isEmpty";
import { formatPrice } from "../utils";
import cn from "classnames";

import { HorizontalScrollbar } from "../../../components/HorizontalScrollbar";

// Attributes that don't start with 'attributes.*'
const UNPREFIXED_ATTRIBUTES = ["sku", "product_type"];

let Icon = () => (
    <div className="flex items-center justify-center w-full py-10">
        <FontAwesomeIcon icon={faBoxFull} size="6x" />
    </div>
);

const ProductCard = ({
    product,
    isEditMode,
    attributes,
    attributeOperator,
    border,
    index,
}) => {
    const name = product?.attributes?.product_name;
    const availableVariants = product?.variants;

    const image = product?.resources?.find(
        (resource) => resource?.type === "image"
    )?.location;

    const price =
        product?.fromPrices?.price?.inc ||
        product?.prices?.cheapestPrice?.inc ||
        product?.prices?.price?.inc ||
        0;

    const wasPrice = product?.prices?.wasPrice?.incTax;

    return (
        <div
            data-testid="product-card"
            className="max-w-[270px] min-w-[270px] lg:max-w-[312px] lg:min-w-[312px] flex  border border-brand-tertiary rounded-[10px] bg-brand-white overflow-hidden w-full flex-col"
        >
            <a
                data-testid="product-card__link"
                href={`/products/${product?.slug}`}
                onClick={(e) => e.preventDefault()}
                className="flex flex-col gap-y-[10px]"
            >
                <img
                    data-testid="product-card__image"
                    src={image || "/images/placeholder.png"}
                    alt={name}
                    className="aspect-square w-full rounded"
                />
            </a>

            <div className="flex flex-col p-[15px] flex-grow">
                <div className="mb-[10px] flex flex-col gap-y-[5px] flex-grow">
                    <a
                        data-testid="product-card__link"
                        href={`/products/${product?.slug}`}
                        type="button"
                        onClick={(e) => e.preventDefault()}
                        className=" flex flex-col gap-y-[10px] !text-brand-black !no-underline !text-[24px] !leading-[30px] !font-bold"
                    >
                        {name}
                    </a>

                    <div className="flex flex-col flex-grow">
                        <p className="text-base">Part No. {product?.sku}</p>

                        <div className="flex flex-row items-end gap-y-[10px] gap-x-[15px]">
                            {price && (
                                <div className="flex items-end gap-x-[5px]">
                                    <span
                                        data-testid="product-card__now-price"
                                        className={cn(
                                            "text-2xl leading-[30px] font-bold text-brand-quaternary"
                                        )}
                                    >
                                        {availableVariants ? "From " : ""}
                                        {formatPrice(price)}
                                    </span>
                                    <span
                                        data-testid="tax-message"
                                        className="text-base font-bold leading-[25px] text-brand-quaternary whitespace-nowrap"
                                    >
                                        Incl. VAT
                                    </span>
                                </div>
                            )}

                            {!availableVariants && wasPrice && (
                                <span
                                    data-testid="product-card__was-price"
                                    className={cn(
                                        "text-brand-danger text-base whitespace-nowrap"
                                    )}
                                >
                                    Was{" "}
                                    <span className="line-through">
                                        {formatPrice(wasPrice)}
                                    </span>
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex flex-col gap-y-[10px]">
                    <button
                        data-testid="product-card__add-to-basket"
                        className="relative inline-flex items-center justify-center whitespace-nowrap rounded-[5px] text-base font-bold leading-[18px] border transition-colors active:ring-4 disabled:opacity-25 disabled:cursor-not-allowed bg-brand-secondary text-brand-white border-brand-secondary hover:bg-brand-secondary-darken active:bg-brand-secondary-darken active:ring-brand-secondary-darken/40 h-[50px] px-[30px] w-full"
                    >
                        Add To Basket
                    </button>
                </div>
            </div>
        </div>
    );
};

const Hits = (props) => {
    const { hits, isEditMode, fallbackImage, border } = props;

    const inStorefront = !window?.location?.pathname.includes("/content/edit/");

    return hits.length > 0 ? (
        <>
            {hits.map((hit, index) => (
                <ProductCard
                    key={hit?.slug || `hit_${index}`}
                    product={hit}
                    isEditMode={isEditMode}
                    fallbackImage={fallbackImage}
                    border={border}
                    index={index}
                />
            ))}
        </>
    ) : isEditMode || !inStorefront ? (
        <Icon />
    ) : null;
};
const CustomHits = connectHits(Hits);

const ProductsContent = ({
    numberOfProducts = 4,
    attributes = [],
    attributeOperator = "and",
    border,
    isEditMode,
}) => {
    const config = useConfig();
    const algolia = config.get("algolia") || {};

    const { applicationId, searchApiKey, indexName } = algolia;

    const searchClient = algoliasearch(applicationId, searchApiKey);

    const [facetFilters, setFacetFilters] = useState();

    useEffect(() => {
        if (attributes?.length > 0) {
            const categories = attributes?.reduce(
                (acc, { attribute, value }) => {
                    const attributeKey = UNPREFIXED_ATTRIBUTES.includes(
                        attribute
                    )
                        ? attribute
                        : `attributes.${attribute}`;
                    if (value.length > 0) acc.push(`${attributeKey}:${value}`);

                    return acc;
                },
                []
            );

            setFacetFilters(categories);
        }
    }, [attributes]);

    if (isEmpty(algolia)) {
        console.error(
            "No Algolia configuration defined. Please refer to the Peracto documentation."
        );
    }

    return useMemo(
        () =>
            isEmpty(algolia) ? null : (
                <InstantSearch
                    searchClient={searchClient}
                    indexName={indexName}
                >
                    <Configure
                        hitsPerPage={numberOfProducts}
                        facetFilters={
                            facetFilters
                                ? attributeOperator === "and"
                                    ? facetFilters
                                    : [facetFilters]
                                : null
                        }
                    />

                    <CustomHits isEditMode={isEditMode} border={border} />
                </InstantSearch>
            ),
        [
            algolia,
            searchClient,
            indexName,
            numberOfProducts,
            border,
            facetFilters,
            attributeOperator,
            isEditMode,
        ]
    );
};

const ProductsRenderer = ({ state, ...props }) => {
    const { title = "Products" } = state;

    const [showHorizontalScrollbar, setShowHorizontalScrollbar] =
        useState(false);

    const containerRef = useRef(null);
    const scrollbarRef = useRef(null);

    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    // --- Update button states ---
    const updateScrollButtons = () => {
        const container = containerRef.current;
        if (!container) return;
        setCanScrollLeft(container.scrollLeft > 0);
        setCanScrollRight(
            container.scrollLeft <
                container.scrollWidth - container.clientWidth - 1
        );
    };

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        let isDragging = false;
        let startX = 0;
        let scrollLeftStart = 0;
        let moved = false; // Track if user actually dragged

        const onMouseDown = (e) => {
            isDragging = true;
            moved = false;
            startX = e.pageX - container.offsetLeft;
            scrollLeftStart = container.scrollLeft;
            container.classList.add("cursor-grabbing");
            e.preventDefault();
        };

        const onMouseMove = (e) => {
            if (!isDragging) return;
            const x = e.pageX - container.offsetLeft;
            const walk = x - startX;
            if (Math.abs(walk) > 2) moved = true; // small threshold to count as drag
            container.scrollLeft = scrollLeftStart - walk;
        };

        const onMouseUp = () => {
            isDragging = false;
            container.classList.remove("cursor-grabbing");
        };

        const onTouchStart = (e) => {
            isDragging = true;
            moved = false;
            startX = e.touches[0].pageX - container.offsetLeft;
            scrollLeftStart = container.scrollLeft;
        };

        const onTouchMove = (e) => {
            if (!isDragging) return;
            const x = e.touches[0].pageX - container.offsetLeft;
            const walk = x - startX;
            if (Math.abs(walk) > 2) moved = true;
            container.scrollLeft = scrollLeftStart - walk;
        };

        const onTouchEnd = () => (isDragging = false);

        // --- Prevent clicks on links if dragging ---
        const onClickCapture = (e) => {
            if (moved) {
                // prevent link clicks if dragging occurred
                e.preventDefault();
                e.stopPropagation();
            }
        };

        container.addEventListener("mousedown", onMouseDown);
        container.addEventListener("mousemove", onMouseMove);
        container.addEventListener("mouseup", onMouseUp);
        container.addEventListener("mouseleave", onMouseUp);
        container.addEventListener("touchstart", onTouchStart);
        container.addEventListener("touchmove", onTouchMove);
        container.addEventListener("touchend", onTouchEnd);

        container.addEventListener("click", onClickCapture, true); // capture phase

        return () => {
            container.removeEventListener("mousedown", onMouseDown);
            container.removeEventListener("mousemove", onMouseMove);
            container.removeEventListener("mouseup", onMouseUp);
            container.removeEventListener("mouseleave", onMouseUp);
            container.removeEventListener("touchstart", onTouchStart);
            container.removeEventListener("touchmove", onTouchMove);
            container.removeEventListener("touchend", onTouchEnd);
            container.removeEventListener("click", onClickCapture, true);
        };
    }, []);

    useEffect(() => {
        setTimeout(() => {
            updateScrollButtons();
            setShowHorizontalScrollbar(true);
        }, 500);
    }, [state]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        updateScrollButtons();

        container.addEventListener("scroll", updateScrollButtons);
        window.addEventListener("resize", updateScrollButtons);

        return () => {
            container.removeEventListener("scroll", updateScrollButtons);
            window.removeEventListener("resize", updateScrollButtons);
        };
    }, [
        state.numberOfProducts,
        state.attributes,
        state.attributeOperator,
        state.border,
        state.isEditMode,
    ]);

    return (
        <div className="flex flex-col gap-y-[30px]">
            {title && (
                <h2 className="text-[30px] leading-[36px] lg:text-[40px] lg:leading-[46px] font-bold text-center">
                    {title}
                </h2>
            )}
            <div className="relative">
                {(canScrollLeft || canScrollRight) && (
                    <>
                        {/* Left Button */}
                        <button
                            onClick={() => scrollbarRef.current?.scrollBy(-270)}
                            className="absolute z-[10] top-[200px] lg:-left-[25px] left-0 h-[50px] w-[50px] flex items-center justify-center bg-brand-quaternary/70 rounded-full hover:bg-brand-quaternary-darken text-brand-white disabled:opacity-50"
                            disabled={!canScrollLeft}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </button>

                        {/* Right Button */}
                        <button
                            onClick={() => scrollbarRef.current?.scrollBy(270)}
                            className="absolute z-[10] top-[200px] lg:-right-[25px] right-0 h-[50px] w-[50px] flex items-center justify-center bg-brand-quaternary/70 rounded-full hover:bg-brand-quaternary-darken text-brand-white disabled:opacity-50"
                            disabled={!canScrollRight}
                        >
                            <FontAwesomeIcon icon={faArrowRight} />
                        </button>
                    </>
                )}

                <div
                    className="overflow-hidden flex gap-x-[15px] lg:gap-x-[30px] items-stretch"
                    ref={containerRef}
                >
                    <ProductsContent {...state} {...props} />
                </div>

                {showHorizontalScrollbar && (
                    <HorizontalScrollbar
                        containerRef={containerRef}
                        ref={scrollbarRef}
                    />
                )}
            </div>
        </div>
    );
};

export default ProductsRenderer;
